/**
 * src/shared/lib/changeQueue.ts
 *
 * Atomic IDB-backed SyncQueue — safe for concurrent tabs.
 *
 * Uses idb-keyval `update()` for atomic read-modify-write (never get+set).
 * Namespaced under 'estimatix-sync-db'/'queue' to isolate from legacy bg2_* keys.
 *
 * All operations degrade gracefully on IDB failure (console.warn, never throw).
 * T-04-10 mitigation: IDB write failure is logged; the app continues working.
 */

import { update, get, set, createStore } from 'idb-keyval';
import type { ChangeRecord } from '@/entities/sync/model/types';

// Namespace isolation from legacy bg2_* IDB keys (RESEARCH open Q3)
const queueStore = createStore('estimatix-sync-db', 'queue');
const QUEUE_KEY = 'estimatix-sync-queue';

/**
 * Enqueue a change record.
 * Atomic: uses update() for safe cross-tab concurrent enqueues.
 * Idempotent by id: if a record with the same id already exists, it is NOT duplicated.
 * Degrades gracefully on IDB failure (T-04-10).
 */
export async function enqueueChange(record: ChangeRecord): Promise<void> {
  try {
    await update<ChangeRecord[]>(
      QUEUE_KEY,
      (queue) => {
        const existing = queue ?? [];
        // Idempotent: do not add duplicate ids
        if (existing.some((r) => r.id === record.id)) {
          return existing;
        }
        return [...existing, record];
      },
      queueStore,
    );
  } catch (e) {
    console.warn('[changeQueue] enqueueChange failed — IDB unavailable. Edit retained in project state.', e);
  }
}

/**
 * Read the current queue without modifying it.
 * Returns empty array on IDB failure.
 */
export async function peekQueue(): Promise<ChangeRecord[]> {
  try {
    return (await get<ChangeRecord[]>(QUEUE_KEY, queueStore)) ?? [];
  } catch (e) {
    console.warn('[changeQueue] peekQueue failed — returning empty queue.', e);
    return [];
  }
}

/**
 * Remove successfully flushed records by id. Atomic.
 * Idempotent: re-running with the same ids is a no-op.
 * Only removes ids in the flushedIds set; other records are preserved.
 * T-04-12 mitigation: only called AFTER server confirms receipt (200 response).
 */
export async function dequeueFlushed(flushedIds: Set<string>): Promise<void> {
  try {
    await update<ChangeRecord[]>(
      QUEUE_KEY,
      (queue) => (queue ?? []).filter((r) => !flushedIds.has(r.id)),
      queueStore,
    );
  } catch (e) {
    console.warn('[changeQueue] dequeueFlushed failed — queue may have stale records on retry.', e);
  }
}

/**
 * Clear the entire queue.
 * Only call after all records confirmed received by the server.
 */
export async function clearQueue(): Promise<void> {
  try {
    await set(QUEUE_KEY, [], queueStore);
  } catch (e) {
    console.warn('[changeQueue] clearQueue failed — IDB unavailable.', e);
  }
}
